import { useEffect, useRef } from 'react';
import { useShouldRenderAds } from '@/lib/adsConfig';
import { cookieConsent } from '@/lib/cookieConsent';

interface AdSenseProps {
  slot: string;
  format?: 'auto' | 'rectangle' | 'vertical' | 'horizontal';
  responsive?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

export const AdSense = ({ 
  slot, 
  format = 'auto', 
  responsive = true, 
  style,
  className = ''
}: AdSenseProps) => {
  const shouldRender = useShouldRenderAds();
  const containerRef = useRef<HTMLDivElement>(null);
  const adInitializedRef = useRef(false);

  useEffect(() => {
    if (!shouldRender) return;

    const initializeAd = () => {
      // Check cookie consent before initializing ads
      if (!cookieConsent.hasConsent()) {
        return;
      }

      if (adInitializedRef.current) return;
      
      const container = containerRef.current;
      if (!container) return;

      // Check if the ins element already has ads (AdSense internal check)
      const insElement = container.querySelector('ins.adsbygoogle');
      if (insElement && insElement.hasAttribute('data-adsbygoogle-status')) {
        adInitializedRef.current = true;
        return;
      }

      // Check if container has dimensions
      const rect = container.getBoundingClientRect();
      const hasWidth = rect.width > 0 || container.offsetWidth > 0;
      const hasHeight = rect.height > 0 || container.offsetHeight > 0;
      
      // For responsive ads, we need at least some width
      // For fixed-size ads, we need both width and height
      if (responsive) {
        if (!hasWidth) {
          // Retry after a short delay if container doesn't have width yet
          // Limit retries to prevent infinite loops
          if (!container.hasAttribute('data-retry-count')) {
            container.setAttribute('data-retry-count', '0');
          }
          const retryCount = parseInt(container.getAttribute('data-retry-count') || '0', 10);
          if (retryCount < 5) {
            container.setAttribute('data-retry-count', String(retryCount + 1));
            setTimeout(initializeAd, 200);
          }
          return;
        }
      } else {
        if (!hasWidth || !hasHeight) {
          // Retry after a short delay if container doesn't have dimensions yet
          // Limit retries to prevent infinite loops
          if (!container.hasAttribute('data-retry-count')) {
            container.setAttribute('data-retry-count', '0');
          }
          const retryCount = parseInt(container.getAttribute('data-retry-count') || '0', 10);
          if (retryCount < 5) {
            container.setAttribute('data-retry-count', String(retryCount + 1));
            setTimeout(initializeAd, 200);
          }
          return;
        }
      }

      try {
        // @ts-expect-error AdSense global array push
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        adInitializedRef.current = true;
      } catch (error) {
        // Silently handle AdSense errors to avoid console spam
        // Only log in development
        if (process.env.NODE_ENV === 'development') {
          console.error('AdSense error:', error);
        }
      }
    };

    // Use requestAnimationFrame to ensure layout is complete
    requestAnimationFrame(() => {
      // Add a small delay to ensure container is fully rendered
      setTimeout(initializeAd, 50);
    });

    const container = containerRef.current;

    // Also set up an IntersectionObserver as a fallback
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !adInitializedRef.current) {
            initializeAd();
          }
        });
      },
      { threshold: 0.01 }
    );

    if (container) {
      observer.observe(container);
    }

    return () => {
      if (container) {
        observer.unobserve(container);
      }
    };
  }, [responsive, shouldRender]);

  if (!shouldRender) return null;

  return (
    <div ref={containerRef} className={`adsense-container ${className}`} style={style}>
      <ins
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client="ca-pub-2717395554523712"
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive.toString()}
      />
    </div>
  );
};

// Predefined ad slot components for consistent placement
export const LeaderboardAd = ({ className }: { className?: string }) => (
  <AdSense
    slot="8985994760"
    format="auto"
    className={`min-h-[90px] ${className || ''}`}
  />
);

export const RectangleAd = ({ className }: { className?: string }) => (
  <AdSense
    slot="2021378586"
    format="rectangle"
    style={{ width: '300px', height: '250px', display: 'inline-block' }}
    responsive={false}
    className={`min-h-[250px] ${className || ''}`}
  />
);

export const SidebarAd = ({ className }: { className?: string }) => (
  <AdSense
    slot="8395215240"
    format="vertical"
    style={{ width: '160px', height: '600px', display: 'inline-block' }}
    responsive={false}
    className={`min-h-[600px] ${className || ''}`}
  />
);

export const InContentAd = ({ className }: { className?: string }) => (
  <AdSense
    slot="1498672391"
    format="auto"
    className={`min-h-[280px] my-8 ${className || ''}`}
  />
);

// Mobile-specific ad components
export const MobileBannerAd = ({ className }: { className?: string }) => (
  <AdSense
    slot="1498672391" // Using in-content slot for mobile banner
    format="auto"
    className={`min-h-[50px] block md:hidden ${className || ''}`}
  />
);

// Responsive ads that adapt to screen size
export const ResponsiveDisplayAd = ({ className }: { className?: string }) => {
  const shouldRender = useShouldRenderAds();
  if (!shouldRender) return null;

  return (
    <div className={className}>
      {/* Mobile banner */}
      <div className="block md:hidden">
        <AdSense
          slot="1498672391"
          format="auto"
          style={{ minHeight: '50px' }}
          className="w-full"
        />
      </div>
      {/* Desktop rectangle */}
      <div className="hidden md:block">
        <AdSense
          slot="2021378586"
          format="rectangle" 
          style={{ width: '300px', height: '250px', display: 'inline-block' }}
          responsive={false}
          className="min-h-[250px]"
        />
      </div>
    </div>
  );
};

// Sidebar ad that only shows on desktop
export const DesktopSidebarAd = ({ className }: { className?: string }) => {
  const shouldRender = useShouldRenderAds();
  if (!shouldRender) return null;

  return (
    <div className={`hidden lg:block ${className || ''}`}>
      <AdSense
        slot="8395215240"
        format="vertical"
        style={{ width: '160px', height: '600px', display: 'inline-block' }}
        responsive={false}
        className="min-h-[600px]"
      />
    </div>
  );
};
